/**
 * Upload Protection & Rate Limiting Module
 * Prevents abuse, spam, and bot attacks
 */

(function(window) {
    'use strict';

    // ========================================
    // CONFIGURATION
    // ========================================
    const CONFIG = {
        // Rate Limiting
        MAX_UPLOADS_PER_SESSION: 10,           // Maksimum session başına upload
        MAX_UPLOADS_PER_MINUTE: 3,             // 1 dakika içinde max 3 upload
        COOLDOWN_SECONDS: 10,                  // Upload'lar arası minimum süre (saniye)
        
        // File Protection
        MAX_FILE_SIZE: 5 * 1024 * 1024,        // 5MB
        MIN_FILE_SIZE: 1024,                   // 1KB (çok küçük dosyaları engelle)
        ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png'],
        
        // Session Protection
        SESSION_TIMEOUT: 30 * 60 * 1000,       // 30 dakika
        
        // Duplicate Protection
        CHECK_DUPLICATES: true,                // Aynı dosyayı tekrar yüklemeyi engelle
    };

    // ========================================
    // STATE MANAGEMENT
    // ========================================
    let uploadHistory = [];
    let sessionStartTime = Date.now();
    let lastUploadTime = 0;
    let uploadedFileHashes = new Set();
    let isUploadInProgress = false;

    /**
     * Initialize session data from localStorage
     */
    function initSession() {
        try {
            const stored = localStorage.getItem('uploadSession');
            if (stored) {
                const session = JSON.parse(stored);
                
                // Check if session is expired
                if (Date.now() - session.startTime < CONFIG.SESSION_TIMEOUT) {
                    sessionStartTime = session.startTime;
                    uploadHistory = session.history || [];
                    uploadedFileHashes = new Set(session.hashes || []);
                    console.log('📦 Session restored:', {
                        uploads: uploadHistory.length,
                        startTime: new Date(sessionStartTime).toLocaleTimeString()
                    });
                } else {
                    console.log('⏰ Session expired, starting new session');
                    resetSession();
                }
            }
        } catch (error) {
            console.error('Session init error:', error);
            resetSession();
        }
    }

    /**
     * Save session to localStorage
     */
    function saveSession() {
        try {
            const session = {
                startTime: sessionStartTime,
                history: uploadHistory,
                hashes: Array.from(uploadedFileHashes)
            };
            localStorage.setItem('uploadSession', JSON.stringify(session));
        } catch (error) {
            console.error('Session save error:', error);
        }
    }

    /**
     * Reset session
     */
    function resetSession() {
        sessionStartTime = Date.now();
        uploadHistory = [];
        uploadedFileHashes.clear();
        saveSession();
    }

    /**
     * Generate simple hash for file (for duplicate detection)
     */
    async function generateFileHash(file) {
        const chunkSize = 1024;
        const chunk = file.slice(0, chunkSize);
        const text = await chunk.text();
        return `${file.name}_${file.size}_${text.length}`;
    }

    /**
     * Check if file is duplicate
     */
    async function isDuplicateFile(file) {
        if (!CONFIG.CHECK_DUPLICATES) return false;
        
        const hash = await generateFileHash(file);
        return uploadedFileHashes.has(hash);
    }

    /**
     * Mark file as uploaded
     */
    async function markFileAsUploaded(file) {
        const hash = await generateFileHash(file);
        uploadedFileHashes.add(hash);
        saveSession();
    }

    /**
     * Check rate limiting
     */
    function checkRateLimit() {
        const now = Date.now();
        const errors = [];

        // 1. Check if upload is already in progress
        if (isUploadInProgress) {
            errors.push({
                code: 'UPLOAD_IN_PROGRESS',
                message: 'Bir yükleme zaten devam ediyor. Lütfen bekleyin.',
                retryAfter: 3
            });
        }

        // 2. Check cooldown period
        if (lastUploadTime > 0) {
            const timeSinceLastUpload = (now - lastUploadTime) / 1000;
            if (timeSinceLastUpload < CONFIG.COOLDOWN_SECONDS) {
                const waitTime = Math.ceil(CONFIG.COOLDOWN_SECONDS - timeSinceLastUpload);
                errors.push({
                    code: 'COOLDOWN_ACTIVE',
                    message: `Lütfen ${waitTime} saniye bekleyin.`,
                    retryAfter: waitTime
                });
            }
        }

        // 3. Check session limit
        if (uploadHistory.length >= CONFIG.MAX_UPLOADS_PER_SESSION) {
            errors.push({
                code: 'SESSION_LIMIT',
                message: `Oturum limiti aşıldı (${CONFIG.MAX_UPLOADS_PER_SESSION} upload/oturum). Lütfen sayfayı yenileyin.`,
                retryAfter: null
            });
        }

        // 4. Check per-minute limit
        const oneMinuteAgo = now - 60000;
        const recentUploads = uploadHistory.filter(u => u.timestamp > oneMinuteAgo);
        if (recentUploads.length >= CONFIG.MAX_UPLOADS_PER_MINUTE) {
            errors.push({
                code: 'RATE_LIMIT',
                message: `Çok fazla yükleme. 1 dakika içinde en fazla ${CONFIG.MAX_UPLOADS_PER_MINUTE} dosya yükleyebilirsiniz.`,
                retryAfter: 60
            });
        }

        return {
            allowed: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Validate file before upload
     */
    async function validateUpload(file) {
        const errors = [];

        // 1. File size validation
        if (file.size > CONFIG.MAX_FILE_SIZE) {
            errors.push({
                code: 'FILE_TOO_LARGE',
                message: `Dosya boyutu çok büyük (max ${CONFIG.MAX_FILE_SIZE / 1024 / 1024}MB)`
            });
        }

        if (file.size < CONFIG.MIN_FILE_SIZE) {
            errors.push({
                code: 'FILE_TOO_SMALL',
                message: 'Dosya boyutu çok küçük'
            });
        }

        // 2. File type validation
        if (!CONFIG.ALLOWED_TYPES.includes(file.type.toLowerCase())) {
            errors.push({
                code: 'INVALID_TYPE',
                message: 'Sadece JPG ve PNG formatları kabul edilir'
            });
        }

        // 3. Check for duplicates
        if (await isDuplicateFile(file)) {
            errors.push({
                code: 'DUPLICATE_FILE',
                message: 'Bu dosya zaten yüklendi. Farklı bir dosya seçin.'
            });
        }

        // 4. Check rate limiting
        const rateCheck = checkRateLimit();
        if (!rateCheck.allowed) {
            errors.push(...rateCheck.errors);
        }

        return {
            valid: errors.length === 0,
            errors: errors
        };
    }

    /**
     * Record successful upload
     */
    function recordUpload(file) {
        const now = Date.now();
        
        uploadHistory.push({
            timestamp: now,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type
        });

        lastUploadTime = now;
        markFileAsUploaded(file);
        saveSession();

        console.log('📊 Upload recorded. Total: ' + uploadHistory.length);
    }

    /**
     * Start upload (set flag)
     */
    function startUpload() {
        isUploadInProgress = true;
    }

    /**
     * End upload (clear flag)
     */
    function endUpload() {
        isUploadInProgress = false;
    }

    /**
     * Show rate limit error
     */
    function showRateLimitError(errors) {
        const primaryError = errors[0];
        
        if (typeof ValidationModule !== 'undefined') {
            ValidationModule.showErrors([{
                field: 'rate_limit',
                message: primaryError.message
            }]);
        } else {
            alert(primaryError.message);
        }

        console.warn('🚫 Rate limit exceeded:', errors);
    }

    /**
     * Get upload statistics
     */
    function getStats() {
        return {
            totalUploads: uploadHistory.length,
            sessionStartTime: sessionStartTime,
            lastUploadTime: lastUploadTime,
            remainingUploads: CONFIG.MAX_UPLOADS_PER_SESSION - uploadHistory.length,
            cooldownActive: lastUploadTime > 0 && (Date.now() - lastUploadTime) < (CONFIG.COOLDOWN_SECONDS * 1000)
        };
    }

    /**
     * Clear old upload history (older than 1 hour)
     */
    function cleanupHistory() {
        const oneHourAgo = Date.now() - (60 * 60 * 1000);
        uploadHistory = uploadHistory.filter(u => u.timestamp > oneHourAgo);
        saveSession();
    }

    // Initialize on load
    initSession();

    // Cleanup every 5 minutes
    setInterval(cleanupHistory, 5 * 60 * 1000);

    // Export to global scope
    window.UploadProtection = {
        validateUpload: validateUpload,
        recordUpload: recordUpload,
        startUpload: startUpload,
        endUpload: endUpload,
        showRateLimitError: showRateLimitError,
        getStats: getStats,
        resetSession: resetSession,
        checkRateLimit: checkRateLimit
    };

    console.log('🛡️ Upload Protection Module loaded');
    console.log('📊 Current stats:', getStats());

})(window);
